import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash';

import { HIGHLIGHT_TYPES, HIGHLIGHT_ERROR, HIGHLIGHT_SUCCESS } from '../../../constants/exercise';
import MathContent from '../../math-content';

import RadioCorrectIcon from './assets/checkmarkCircle.svg?react';
import RadioErrorIcon from './assets/multiplyCircled.svg?react';
import RadioUnselectedIcon from './assets/radioButton.svg?react';
import RadioSelectedIcon from './assets/radioButtonChecked.svg?react';

const textColors = {
    false: 'var(--gray800)',
    true: 'var(--gray700)',
};

const statusColors = {
    true: {
        // disabled
        normal: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--gray700)',
                hoverBorderColor: 'var(--gray200)',
                hoverBackgroundColor: 'var(--color-white)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--gray700)',
                hoverBorderColor: 'var(--gray200)',
                hoverBackgroundColor: 'var(--color-white)',
            },
        },
        success: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray800)',
                backgroundColor: 'var(--gray50)',
                checkmarkColor: 'var(--gray800)',
                hoverBorderColor: 'var(--gray800)',
                hoverBackgroundColor: 'var(--gray50)',
            },
        },
        error: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
        },
    },
    false: {
        // not disabled
        normal: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--blue500)',
                backgroundColor: 'var(--blue50)',
                checkmarkColor: 'var(--blue500)',
                hoverBorderColor: 'var(--blue500)',
                hoverBackgroundColor: 'var(--blue50)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--blue500)',
                hoverBorderColor: 'var(--gray700)',
                hoverBackgroundColor: 'var(--gray50)',
            },
        },
        success: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
        },
        error: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
        },
    },
};

const Container = styled.div`
    display: flex;
    position: relative;
    width: 100%;
    border-radius: 4px;
    border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.borderColor`)};

    min-height: 44px;
    min-width: 112px;

    font-size: 16px;
    color: ${(props) => get(textColors, props.disabled && props.highlight === 'normal')};
    background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.backgroundColor`)};
    line-height: 1;

    cursor: ${(props) => (props.disabled ? 'initial' : 'pointer')};
    &:hover {
        border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBorderColor`)};
        background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBackgroundColor`)};
    }
    &:focus-within {
        border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBorderColor`)};
        background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBackgroundColor`)};
        box-shadow: var(--outline);
    }
`;

const Radio = styled.input`
    position: absolute;
    opacity: 0;
`;

const Label = styled.label`
    & > svg {
        margin-left: 12px;
        margin-right: 8px;
        margin-top: 12px;
        width: 18px;
        min-width: 18px;
        height: 18px;
        fill: ${(props) => {
            return get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.checkmarkColor`);
        }};
    }

    p:first-of-type {
        margin: 0;
    }

    display: inline-flex;
    cursor: ${(props) => (props.disabled ? 'initial' : 'pointer')};
    overflow-x: auto;
`;

//.mjx-chtml serve per evitare scroll su particolari matematiche LAZE-1136 dentro scelta multipla
const ContentWrapper = styled.div`
    overflow-x: auto;
    margin: 8px 16px 8px 0px;
    overflow-y: hidden;
    .mjx-chtml {
        overflow: hidden;
    }

    img {
        margin: 0 8px;
        vertical-align: top;
    }
`;

const selectIcon = (highlighted, checked) => {
    if (highlighted === HIGHLIGHT_SUCCESS) {
        return RadioCorrectIcon;
    }
    if (highlighted === HIGHLIGHT_ERROR) {
        return RadioErrorIcon;
    }
    if (checked) {
        return RadioSelectedIcon;
    }
    return RadioUnselectedIcon;
};

const RadioOption = (props) => {
    const { id, disabled = false, selected = false, checked = false, highlight = 'normal', children, name, onClick, value } = props;

    const RadioIcon = selectIcon(highlight, checked);
    return (
        <Container
            {...props}
            disabled={disabled}
            selected={selected}
            highlight={highlight}
            onClick={(e) => {
                e.preventDefault();
                !disabled && onClick(value);
            }}
        >
            <Radio
                id={`${id}_${name}_${value}`}
                disabled={disabled}
                type="radio"
                checked={checked}
                name={name}
                value={value}
                aria-invalid={highlight === HIGHLIGHT_TYPES.ERROR}
                onChange={() => {}}
            />

            <Label htmlFor={`${id}_${name}_${value}`} disabled={disabled} highlight={highlight} selected={selected}>
                <RadioIcon />
                <ContentWrapper>
                    <VerticalAlignedMath>{children}</VerticalAlignedMath>
                </ContentWrapper>
            </Label>
        </Container>
    );
};

const VerticalAlignedMath = styled(MathContent)`
    .mjx-chtml {
        vertical-align: middle;
    }
`;

RadioOption.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
    name: PropTypes.string,
    disabled: PropTypes.bool,
    selected: PropTypes.bool,
    checked: PropTypes.bool,
    highlight: PropTypes.oneOf(HIGHLIGHT_TYPES),
    onClick: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
};
export default RadioOption;
